import { blogPath, ARTICLE_SLUG_ALIASES } from '../../lib/blogTopics';
import { blogImages, blogText, subscribeBlogUpdates } from '../../lib/blogData';
// export default BlogDetail;
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import supabase from "../../lib/supabaseClient";
import "./BlogDetail.scss";
import ImageSlider from "../common/ImageSlider/ImageSlider";
import useTranslate from "../../hooks/useTranslate";

const BlogDetail = () => {
  const { slug, topic } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (blog) window.dispatchEvent(new CustomEvent('kavak:article-seo', { detail: { path: window.location.pathname, article: blog } }));
  }, [blog]);

  const isRTL = i18n.language === "fa";
  const { translate } = useTranslate();

  useEffect(() => {
    let active = true;
    const refresh = () => fetchBlogDetail(() => active, false);
    fetchBlogDetail(() => active, true);
    const unsubscribe = subscribeBlogUpdates(refresh);
    return () => { active = false; unsubscribe(); };
  }, [slug, topic]);

  const fetchBlogDetail = async (isActive = () => true, showLoading = true) => {
    try {
      if (showLoading) { setLoading(true); setError(null); setBlog(null); }

      const { data, error } = await supabase
        .from("blogs")
        .select("*")
        .eq("slug", ARTICLE_SLUG_ALIASES[slug] || slug)
        .eq("is_published", true)
        .maybeSingle();

      if (!isActive()) return;
      if (error) throw error;

      if (!data) {
        window.dispatchEvent(new CustomEvent('kavak:article-seo', { detail: { path: window.location.pathname, article: { notFound: true } } }));
        setError("Blog not found");
        return;
      }

      const canonical = blogPath(data);
      if (window.location.pathname.replace(/^\/(?:fa|en)(?=\/|$)/, "") !== canonical) navigate(canonical, { replace: true });
      setError(null);
      setBlog(data);
    } catch (err) {
      console.error("Error fetching blog:", err);
      if (isActive()) setError(err.message);
    } finally {
      if (isActive()) setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = isRTL ? "fa-IR" : "en-US";

    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="blog-detail-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-container">
        <div className="error-message">
          <h2>{translate("blogNotFound") || "Blog not found"}</h2>
          <button onClick={() => navigate("/blog")} className="back-button">
            {isRTL ? "بازگشت به مقاله‌ها" : "Back to articles"}
          </button>
        </div>
      </div>
    );
  }

  const title = blogText(blog, "title", i18n.language);
  const description = blogText(blog, "description", i18n.language);
  const images = blogImages(blog);
  const coverImage = images[0];

  return (
    <div className="blog-detail-container" dir={isRTL ? "rtl" : "ltr"}>
      <div className="blog-detail-content">
        <button onClick={() => navigate("/blog")} className="back-button">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {isRTL ? "بازگشت به مقاله‌ها" : "Back to articles"}
        </button>

        <article className="blog-article">
          <div className="blog-hero">
            {coverImage && (
              <div className="blog-cover">
                <img src={coverImage} alt={title} />
              </div>
            )}

            <div className="blog-hero-info">
              <strong className="blog-topic">{blog.topic}</strong>
              <h1 className="blog-title">{title}</h1>

              <div className="blog-meta">
                {blog.author && (
                  <span className="blog-author">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {blog.author === "Kavak Editorial" && isRTL ? "تحریریه کاوک" : blog.author}
                  </span>
                )}

                <span className="blog-date">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1046 5 21 5.89543 21 7V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7C3 5.89543 3.89543 5 5 5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {formatDate(blog.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="blog-body">
            {description &&
              description
                .split(/\n+/)
                .filter(Boolean)
                .map((paragraph, index) => paragraph.startsWith("## ") ? <h2 className="blog-body-heading" key={index}>{paragraph.slice(3)}</h2> : <p key={index} style={{whiteSpace:"pre-line"}}>{paragraph}</p>)}
          </div>

          {images.length > 1 && (
            <div className="blog-gallery">
              <h3 className="gallery-title">
                {translate("gallery") || "گالری تصاویر"}
              </h3>
              <ImageSlider images={images} />
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogDetail;
